// modules/RFC_module.js
export default class RFCModule {
  constructor({ qcForm, controller, status = null }) {
    this.qcForm = qcForm;
    this.controller = controller;
    this.status = status;
  }
  handleReasonForChange(form) {
    // Get the changed component key from the form data
    const changedComponentKey = this.getChangedComponentKey(form);
    if (changedComponentKey) {
      // Generate the corresponding RFC key
      const rfcKey = `RFC_${changedComponentKey}`;
      // Add small delay to ensure form processing is complete
      setTimeout(() => {
        this.showRFCComponent(this.qcForm, rfcKey);
      }, 0);
    }
  }

  // NEW: Method to handle form status changes
  handleStatusChange(formData = null, qcResponses = null) {
    if (this.status === "completed" || this.status === "editing") {
      // Show RFC fields that have values even when form is disabled
      this.showRFCFieldsWithValues(formData, qcResponses);
    }
  }

  // NEW: Method to show RFC fields that already have values

  showRFCFieldsWithValues(formData = null, qcResponses = null) {
    let responseData = {};

    // Parse and extract response data
    if (qcResponses) {
      let data = qcResponses.data || qcResponses || {};

      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch (e) {
          console.error("Failed to parse response data:", e);
          data = {};
        }
      }

      responseData = data.data || data || {};
    }

    // Check if formData and components exist
    if (
      !formData ||
      !formData.components ||
      !Array.isArray(formData.components)
    ) {
      console.warn("Invalid formData structure or no components found");
      return;
    }

    // Iterate through all components
    formData.components.forEach((component, componentIndex) => {
      try {
        // Check if component has nested components
        if (component.components && Array.isArray(component.components)) {
          component.components.forEach((question, questionIndex) => {
            try {
              // Check if the key already starts with RFC_, if so don't add another prefix
              const rfcKey = question.key.startsWith("RFC_")
                ? question.key
                : "RFC_" + question.key;
              // Check if response data has this RFC key with a valid value
              const hasValidValue =
                responseData.hasOwnProperty(rfcKey) &&
                responseData[rfcKey] !== null &&
                responseData[rfcKey] !== undefined &&
                responseData[rfcKey].toString().trim() !== "";

              if (hasValidValue) {
                // Show component if it has a valid value
                this.setComponentVisibility(question, true);
              } else {
                // Hide component if no valid value
                this.setComponentVisibility(question, false);
              }
            } catch (questionError) {
              console.error(
                `Error processing question ${questionIndex}:`,
                questionError
              );
              // Continue processing other questions even if one fails
            }
          });
        } else {
          // Handle direct component (not nested)
          const rfcKey = component.key.startsWith("RFC_")
            ? component.key
            : "RFC_" + component.key;

          const hasValidValue =
            responseData.hasOwnProperty(rfcKey) &&
            responseData[rfcKey] !== null &&
            responseData[rfcKey] !== undefined &&
            responseData[rfcKey].toString().trim() !== "";

          if (hasValidValue) {
            this.setComponentVisibility(component, true);
          } else {
            this.setComponentVisibility(component, false);
          }
        }
      } catch (componentError) {
        console.error(
          `Error processing component ${componentIndex}:`,
          componentError
        );
        // Continue processing other components even if one fails
      }
    });
  }

  setComponentVisibility(component, show = false) {
    if (!component) {
      console.warn("No component provided to setComponentVisibility");
      return;
    }

    try {
      // Get the actual component instance
      let componentInstance = component.component || component;

      // If we still don't have a component instance, try to find it in the form
      if (!componentInstance && this.qcForm && component.key) {
        componentInstance = this.qcForm.getComponent(component.key);
      }

      if (!componentInstance) {
        console.warn(`Component instance not found for key: ${component.key}`);
        return;
      }

      if (show) {
        // Show the component
        componentInstance.hidden = false;
      }

      // Try multiple methods to refresh the component display
      if (this.status === "editing") {
        this.refreshComponent(componentInstance);
      }
    } catch (error) {
      console.error(
        `Error setting component visibility for ${component.key}:`,
        error
      );
    }
  }

  // Helper method to refresh component display
  refreshComponent(componentInstance) {
    try {
      // Method 1: Use Form.io's show/hide methods
      if (componentInstance.hidden) {
        if (
          componentInstance.hide &&
          typeof componentInstance.hide === "function"
        ) {
          componentInstance.hide();
        }
      } else {
        if (
          componentInstance.show &&
          typeof componentInstance.show === "function"
        ) {
          componentInstance.show();
        }
      }

      // Method 2: Trigger component redraw
      if (
        componentInstance.triggerRedraw &&
        typeof componentInstance.triggerRedraw === "function"
      ) {
        componentInstance.triggerRedraw();
      }

      // Method 3: Refresh the component
      if (
        componentInstance.refresh &&
        typeof componentInstance.refresh === "function"
      ) {
        componentInstance.refresh();
      }

      // Method 4: Rebuild the component
      if (
        componentInstance.rebuild &&
        typeof componentInstance.rebuild === "function"
      ) {
        componentInstance.rebuild();
      }

      // Method 5: Redraw the component
      if (
        componentInstance.redraw &&
        typeof componentInstance.redraw === "function"
      ) {
        componentInstance.redraw();
      }

      // Method 6: Force form redraw as last resort
      setTimeout(() => {
        this.forceFormRedraw();
      }, 0);
    } catch (error) {
      console.error("Error refreshing component:", error);
    }
  }

  // Alternative method if you need to show/hide all components at once
  showAllRFCFields(formData = null, qcResponses = null) {
    const responseData = this.parseResponseData(qcResponses);

    if (!formData?.components) return;

    const componentsToProcess = this.getAllComponents(formData.components);

    componentsToProcess.forEach((component) => {
      // Only process components that start with RFC_
      if (component.key.startsWith("RFC_")) {
        const hasValidValue = this.hasValidResponseValue(
          responseData,
          component.key
        );
        this.setComponentVisibility(component, hasValidValue);
      }
    });
  }

  // Helper method to parse response data
  parseResponseData(qcResponses) {
    let responseData = {};

    if (qcResponses) {
      let data = qcResponses.data || qcResponses || {};

      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch (e) {
          console.error("Failed to parse response data:", e);
          data = {};
        }
      }

      responseData = data.data || data || {};
    }

    return responseData;
  }

  // Helper method to get all components (including nested ones)
  getAllComponents(components) {
    const allComponents = [];

    components.forEach((component) => {
      if (component.components && Array.isArray(component.components)) {
        // Add nested components
        allComponents.push(...component.components);
        // Recursively get deeply nested components
        allComponents.push(...this.getAllComponents(component.components));
      } else {
        // Add direct component
        allComponents.push(component);
      }
    });

    return allComponents;
  }

  // Helper method to check if response has valid value
  hasValidResponseValue(responseData, rfcKey) {
    return (
      responseData.hasOwnProperty(rfcKey) &&
      responseData[rfcKey] !== null &&
      responseData[rfcKey] !== undefined &&
      responseData[rfcKey].toString().trim() !== ""
    );
  }

  // NEW: Helper method to find all RFC components
  findAllRFCComponents(formComponent, callback) {
    if (formComponent.components && Array.isArray(formComponent.components)) {
      formComponent.components.forEach((component) => {
        if (component.components && Array.isArray(component.components)) {
          component.components.forEach((question) => {
            if (question.key && question.key.startsWith("RFC_")) {
              callback(question, question.key);
            }
          });
        }
      });
    }
  }

  getChangedComponentKey(form) {
    // Check if form has changed flag and get the component key
    if (form.changed && form.changed.component && form.changed.component.key) {
      return form.changed.component.key;
    }
    // Alternative: check flags.changed if available
    if (
      form.flags &&
      form.flags.changed &&
      form.flags.changed.component &&
      form.flags.changed.component.key
    ) {
      return form.flags.changed.component.key;
    }
    // If direct access doesn't work, iterate through data to find changes
    // This assumes you have a way to track original vs current values
    if (form.data) {
      for (const key in form.data) {
        // You might need to implement change detection logic here
        // based on your specific requirements
        return key; // For now, return the first data key as example
      }
    }
    return null;
  }
  showRFCComponent(formComponent, rfcKey) {
    let componentFound = false;
    // Search through components
    if (formComponent.components && Array.isArray(formComponent.components)) {
      formComponent.components.forEach((component) => {
        if (component.components && Array.isArray(component.components)) {
          component.components.forEach((question) => {
            if (question.key === rfcKey) {
              this.setComponentVisibility(question, true);
              componentFound = true;
            }
          });
        }
      });
    }
  }

  forceFormRedraw() {
    // Try multiple redraw methods with different timings
    const redrawMethods = [
      () => this.qcForm.redraw && this.qcForm.redraw(),
      () => this.qcForm.refresh && this.qcForm.refresh(),
      () => this.qcForm.rebuild && this.qcForm.rebuild(),
      () => this.qcForm.triggerRedraw && this.qcForm.triggerRedraw(),
    ];
    // Immediate redraw
    redrawMethods.forEach((method) => {
      try {
        method();
      } catch (error) {
        // Silently continue if method doesn't exist
      }
    });
    // Delayed redraw to ensure changes take effect
    setTimeout(() => {
      redrawMethods.forEach((method) => {
        try {
          method();
        } catch (error) {
          // Silently continue if method doesn't exist
        }
      });
    }, 10);
    // Another delayed redraw as final fallback
    setTimeout(() => {
      if (this.qcForm.redraw && typeof this.qcForm.redraw === "function") {
        this.qcForm.redraw();
      }
    }, 100);
  }

  // Method to hide RFC component when needed
  hideRFCComponent(formComponent, rfcKey) {
    let componentFound = false;
    if (formComponent.components && Array.isArray(formComponent.components)) {
      formComponent.components.forEach((component) => {
        if (component.components && Array.isArray(component.components)) {
          component.components.forEach((question) => {
            if (question.key === rfcKey) {
              this.hideComponent(question);
              componentFound = true;
            }
          });
        }
      });
    }
  }

  hideComponent(component) {
    if (component && component.component) {
      component.component.hidden = true;
      // Remove required validation when hiding
      if (component.component.validate) {
        component.component.validate.required = false;
      }
      // Use hide method if available
      if (component.hide && typeof component.hide === "function") {
        component.hide();
      }
      this.forceFormRedraw();
    }
  }
}
